import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAccount } from './AccountContext';

/**
 * Permission scopes available in the system
 */
export const PERMISSION_SCOPES = {
  'data.upload': 'Upload trips, expenses, maintenance records',
  'data.edit': 'Edit existing records',
  'data.delete': 'Delete records',
  'analytics.view': 'View dashboard and analytics',
  'finance.view': 'View Finance Hub',
  'reports.view': 'View and generate reports',
  'cohosts.manage': 'Add/edit/remove cohosts',
  'agreements.manage': 'Create/modify commission agreements',
  'payments.manage': 'Record and mark payments as paid',
  'settings.manage': 'Edit business settings',
  'billing.manage': 'Manage subscription and billing',
  'team.manage': 'Invite and manage team members',
};

/**
 * Role presets (should match backend)
 */
export const ROLE_PRESETS = {
  owner: {
    name: 'Owner',
    description: 'Full access to everything including billing and account deletion',
    scopes: ['*'],
  },
  admin: {
    name: 'Admin',
    description: 'Full operational access without billing or account deletion',
    scopes: [
      'data.upload', 'data.edit', 'data.delete',
      'analytics.view', 'finance.view', 'reports.view',
      'cohosts.manage', 'agreements.manage', 'payments.manage',
      'settings.manage', 'team.manage',
    ],
  },
  manager: {
    name: 'Manager',
    description: 'Can manage data and cohosts, but not agreements or payments',
    scopes: [
      'data.upload', 'data.edit',
      'analytics.view', 'finance.view', 'reports.view',
      'cohosts.manage',
    ],
  },
  assistant: {
    name: 'Assistant',
    description: 'Can upload and edit data, view everything, but no management',
    scopes: [
      'data.upload', 'data.edit',
      'analytics.view', 'finance.view', 'reports.view',
    ],
  },
  viewer: {
    name: 'Viewer',
    description: 'Read-only access to view data and reports',
    scopes: [
      'analytics.view', 'finance.view', 'reports.view',
    ],
  },
};

const PermissionContext = createContext(null);

export function PermissionProvider({ children }) {
  // Get account context - PermissionProvider must be inside AccountProvider
  const accountContext = useAccount();
  const activeAccount = accountContext?.activeAccount;
  const accountLoading = accountContext?.loading ?? false;

  // SECURITY FIX: Default to minimal permissions (viewer/read-only)
  // instead of owner. This follows DENY-BY-DEFAULT principle.
  const [permissions, setPermissions] = useState({
    role: 'viewer',
    scopes: ['analytics.view', 'finance.view', 'reports.view'],
    isOwner: false,
    accountId: null,
    accountName: null,
    loading: true, // Track loading state to prevent flash of restricted UI
  });

  // Update permissions when active account changes
  useEffect(() => {
    if (activeAccount) {
      // Account loaded successfully - use actual permissions
      setPermissions({
        role: activeAccount.role || 'owner',
        scopes: activeAccount.scopes || activeAccount.permissions || ['*'],
        isOwner: activeAccount.isOwner || activeAccount.role === 'owner',
        accountId: activeAccount.accountId,
        accountName: activeAccount.accountName,
        loading: false,
      });
    } else if (!accountLoading) {
      // Account load completed but no account found
      // For legacy single-user accounts without explicit account record,
      // check if user is authenticated - if so, they're likely the owner
      // But we still default to restricted access until backend confirms
      
      // SECURITY: Do NOT default to owner permissions
      // Backend will reject unauthorized requests anyway
      // UI should reflect restricted state until confirmed
      setPermissions({
        role: 'viewer',
        scopes: ['analytics.view', 'finance.view', 'reports.view'],
        isOwner: false,
        accountId: null,
        accountName: null,
        loading: false,
      });
    }
  }, [activeAccount, accountLoading]);

  /**
   * Check if user has a specific permission scope
   */
  const hasPermission = useCallback((scope) => {
    if (!permissions.scopes) return false;
    // Wildcard means all permissions
    if (permissions.scopes.includes('*')) return true;
    return permissions.scopes.includes(scope);
  }, [permissions.scopes]);

  /**
   * Check if user has ANY of the specified scopes
   */
  const hasAnyPermission = useCallback((scopes) => {
    if (!permissions.scopes) return false;
    if (permissions.scopes.includes('*')) return true;
    return scopes.some(scope => permissions.scopes.includes(scope));
  }, [permissions.scopes]);

  /**
   * Check if user has ALL of the specified scopes
   */
  const hasAllPermissions = useCallback((scopes) => {
    if (!permissions.scopes) return false;
    if (permissions.scopes.includes('*')) return true;
    return scopes.every(scope => permissions.scopes.includes(scope));
  }, [permissions.scopes]);

  /**
   * Check if user is the account owner
   */
  const isOwner = useCallback(() => {
    return permissions.role === 'owner';
  }, [permissions.role]);

  /**
   * Check if user can manage team
   */
  const canManageTeam = useCallback(() => {
    return hasPermission('team.manage');
  }, [hasPermission]);

  /**
   * Check if user can manage billing
   */
  const canManageBilling = useCallback(() => {
    return hasPermission('billing.manage');
  }, [hasPermission]);

  const value = {
    ...permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    isOwner,
    canManageTeam,
    canManageBilling,
  };

  return (
    <PermissionContext.Provider value={value}>
      {children}
    </PermissionContext.Provider>
  );
}

/**
 * Hook to access permission context
 */
export function usePermissions() {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
}

/**
 * Component that conditionally renders children based on permissions
 * 
 * @example
 * <RequirePermission scope="payments.manage">
 *   <MarkAsPaidButton />
 * </RequirePermission>
 */
export function RequirePermission({ scope, scopes, any = false, fallback = null, children }) {
  const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

  let hasAccess = false;

  if (scope) {
    hasAccess = hasPermission(scope);
  } else if (scopes) {
    hasAccess = any ? hasAnyPermission(scopes) : hasAllPermissions(scopes);
  }

  if (!hasAccess) {
    return fallback;
  }

  return children;
}

/**
 * Component that only renders for account owners
 */
export function RequireOwner({ fallback = null, children }) {
  const { isOwner } = usePermissions();

  if (!isOwner()) {
    return fallback;
  }

  return children;
}

/**
 * Higher-order component to wrap a component with permission check
 */
export function withPermission(WrappedComponent, scope, fallback = null) {
  return function PermissionWrappedComponent(props) {
    return (
      <RequirePermission scope={scope} fallback={fallback}>
        <WrappedComponent {...props} />
      </RequirePermission>
    );
  };
}

export default PermissionContext;
